import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { api_url } from "../conf/conf";

const DEFAULT_PROVIDER = "anthropic";
const UPLOAD_BATCH_SIZE = 10;

const initialConfigForm = {
  id: null,
  agentName: "",
  instruction: "",
  vectorStoreName: "",
  provider: DEFAULT_PROVIDER,
  model: "",
};

const initialUploadProgress = {
  active: false,
  done: false,
  totalFiles: 0,
  uploadedFiles: 0,
  totalBatches: 0,
  currentBatch: 0,
  currentBatchFiles: 0,
  batchPercent: 0,
  totalPercent: 0,
};

export default function VectorStoreAdmin() {
  const api = useMemo(() => axios.create({ baseURL: api_url }), []);
  const fileInputRef = useRef(null);
  const selectedCollectionNameRef = useRef("");

  const [configs, setConfigs] = useState([]);
  const [agentsMap, setAgentsMap] = useState({});
  const [providersMap, setProvidersMap] = useState({});
  const [providerModelsMap, setProviderModelsMap] = useState({});
  const [providerStatusesMap, setProviderStatusesMap] = useState({});
  const [localQueue, setLocalQueue] = useState(null);
  const [chatTypeConfigs, setChatTypeConfigs] = useState({});
  const [collections, setCollections] = useState([]);
  const [docs, setDocs] = useState([]);

  const [pageLoading, setPageLoading] = useState(true);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [collectionActionLoading, setCollectionActionLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(initialUploadProgress);

  const [status, setStatus] = useState({ type: "", text: "" });

  const [configForm, setConfigForm] = useState(initialConfigForm);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [selectedCollectionName, setSelectedCollectionName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [expandedConfigs, setExpandedConfigs] = useState({});

  const agentOptions = useMemo(
    () => uniqueNonEmpty([...Object.values(agentsMap), ...configs.map((item) => item.agentName)]),
    [agentsMap, configs]
  );

  const providerOptions = useMemo(
    () => {
      const providerEntries = Object.entries(providersMap || {});

      if (!providerEntries.length) {
        providerEntries.push([DEFAULT_PROVIDER, "Anthropic"]);
      }

      const configProviders = uniqueNonEmpty([
        configForm.provider,
        ...configs.map((item) => item.provider),
      ]);

      configProviders.forEach((providerName) => {
        if (!providerEntries.some(([value]) => value === providerName)) {
          providerEntries.push([providerName, providerName]);
        }
      });

      return providerEntries.map(([value, label]) => {
        const providerStatus = providerStatusesMap?.[value];
        const suffix = providerStatus?.available === false ? " — недоступен" : "";
        return [value, `${label}${suffix}`];
      });
    },
    [providersMap, providerStatusesMap, configs, configForm.provider]
  );

  const selectedProviderStatus = providerStatusesMap?.[configForm.provider] || null;

  const modelOptions = useMemo(
    () => {
      const providerModels = providerModelsMap?.[configForm.provider] || {};

      if (
        !configForm.model ||
        Object.values(providerModels).includes(configForm.model)
      ) {
        return providerModels;
      }

      return { ...providerModels, current: configForm.model };
    },
    [providerModelsMap, configForm.provider, configForm.model]
  );

  const collectionOptions = useMemo(
    () => uniqueNonEmpty(collections.map((item) => getCollectionName(item))),
    [collections]
  );

  const selectedCollectionMeta = useMemo(
    () => collections.find((item) => getCollectionName(item) === selectedCollectionName) || null,
    [collections, selectedCollectionName]
  );

  const activeCollectionsCount = useMemo(
    () => collections.filter((item) => !item.is_deleted).length,
    [collections]
  );

  const activeConfigsCount = useMemo(
    () => configs.filter((item) => item.agentName).length,
    [configs]
  );

  const setSuccess = (text) => setStatus({ type: "success", text });
  const setFailure = (text) => setStatus({ type: "error", text });
  const clearStatus = () => setStatus({ type: "", text: "" });

  const handleRequestError = (error, fallbackText) => {
    setFailure(getErrorMessage(error, fallbackText));
  };

  const loadConfigs = useCallback(async () => {
    setConfigsLoading(true);
    try {
      const response = await api.get("/model-config");
      const payload = getPayloadData(response.data);

      setConfigs(Array.isArray(payload?.configs) ? payload.configs : []);
      setAgentsMap(payload?.agents || {});
      setProvidersMap(payload?.providers || {});
      setProviderModelsMap(
        payload?.providerModels ||
          (payload?.anthropicmodels ? { [DEFAULT_PROVIDER]: payload.anthropicmodels } : {})
      );
      setProviderStatusesMap(payload?.providerStatuses || {});
      setLocalQueue(payload?.localQueue || null);
      setChatTypeConfigs(payload?.chatTypeConfigs || {});
    } catch (error) {
      throw new Error(getErrorMessage(error, "Не удалось загрузить конфиги агентов"));
    } finally {
      setConfigsLoading(false);
    }
  }, [api]);

  const loadCollections = useCallback(async () => {
    setCollectionsLoading(true);
    try {
      const response = await api.get("/vector-store");
      const payload = getPayloadData(response.data);
      const nextCollections = Array.isArray(payload)
        ? payload.filter(isAdminCollection)
        : [];
      const currentSelectedName = selectedCollectionNameRef.current;

      setCollections(nextCollections);

      if (currentSelectedName) {
        const exists = nextCollections.some(
          (item) => getCollectionName(item) === currentSelectedName
        );

        if (!exists) {
          setSelectedCollectionName("");
          setDocs([]);
        }
      }
    } catch (error) {
      throw new Error(getErrorMessage(error, "Не удалось загрузить коллекции"));
    } finally {
      setCollectionsLoading(false);
    }
  }, [api]);

  const loadDocs = useCallback(async (collectionName) => {
    if (!collectionName) {
      setDocs([]);
      return;
    }

    setDocsLoading(true);
    try {
      const response = await api.post("/vector-store/docs", { collectionName });
      const payload = getPayloadData(response.data);
      setDocs(Array.isArray(payload?.docs) ? payload.docs : []);
    } catch (error) {
      setDocs([]);
      throw new Error(getErrorMessage(error, "Не удалось загрузить документы коллекции"));
    } finally {
      setDocsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    selectedCollectionNameRef.current = selectedCollectionName;
  }, [selectedCollectionName]);

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      setPageLoading(true);
      clearStatus();

      try {
        await Promise.all([loadConfigs(), loadCollections()]);
      } catch (error) {
        if (mounted) {
          setFailure(getErrorMessage(error, "Не удалось загрузить страницу"));
        }
      } finally {
        if (mounted) {
          setPageLoading(false);
        }
      }
    };

    bootstrap();

    return () => {
      mounted = false;
    };
  }, [loadCollections, loadConfigs]);

  useEffect(() => {
    loadDocs(selectedCollectionName).catch((error) => {
      setFailure(getErrorMessage(error, "Не удалось загрузить документы"));
    });
  }, [loadDocs, selectedCollectionName]);

  useEffect(() => {
    if (configForm.model) {
      return;
    }

    const firstModel = Object.values(providerModelsMap?.[configForm.provider] || {})[0] || "";

    if (firstModel) {
      setConfigForm((prev) => (prev.model ? prev : { ...prev, model: firstModel }));
    }
  }, [providerModelsMap, configForm.provider, configForm.model]);

  const resetConfigForm = () => {
    setConfigForm(initialConfigForm);
  };

  const toggleConfigExpand = (configKey) => {
    setExpandedConfigs((prev) => ({
      ...prev,
      [configKey]: !prev[configKey],
    }));
  };

  const handleConfigInputChange = (field) => (event) => {
    const value = event.target.value;
    setConfigForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProviderChange = (event) => {
    const nextProvider = event.target.value;
    const firstModel = Object.values(providerModelsMap?.[nextProvider] || {})[0] || "";

    setConfigForm((prev) => ({
      ...prev,
      provider: nextProvider,
      model: firstModel,
    }));
  };

  const handleSaveConfig = async (event) => {
    event.preventDefault();

    const agentName = configForm.agentName.trim();
    const instruction = configForm.instruction.trim();
    const vectorStoreName = configForm.vectorStoreName.trim();
    const provider = configForm.provider.trim();
    const model = configForm.model.trim();

    if (!agentName) {
      setFailure("Укажите имя агента.");
      return;
    }

    if (!instruction) {
      setFailure("Добавьте инструкцию для агента.");
      return;
    }

    if (!model) {
      setFailure("Выберите модель.");
      return;
    }

    setSavingConfig(true);
    clearStatus();

    try {
      const response = await api.post("/model-config", {
        agentName,
        instruction,
        vectorStoreName: vectorStoreName || null,
        provider,
        model,
      });

      const payload = getPayloadData(response.data);
      await loadConfigs();

      setConfigForm({
        id: payload?.id || null,
        agentName: payload?.agentName || agentName,
        instruction: payload?.instruction || instruction,
        vectorStoreName: payload?.vectorStoreName || vectorStoreName,
        provider: payload?.provider || provider,
        model: payload?.model || model,
      });

      setSuccess(
        configForm.id
          ? `Конфиг агента "${payload?.agentName || agentName}" обновлён.`
          : `Конфиг агента "${payload?.agentName || agentName}" создан.`
      );
    } catch (error) {
      handleRequestError(error, "Не удалось сохранить конфиг агента");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleEditConfig = (config) => {
    setConfigForm({
      id: config.id || null,
      agentName: config.agentName || "",
      instruction: config.instruction || "",
      vectorStoreName: config.vectorStoreName || "",
      provider: config.provider || DEFAULT_PROVIDER,
      model: config.model || "",
    });
    clearStatus();
  };

  const handleDeleteConfig = async (config) => {
    const agentName = config?.agentName || "без имени";
    if (!window.confirm(`Удалить конфиг агента "${agentName}"?`)) {
      return;
    }

    setSavingConfig(true);
    clearStatus();

    try {
      if (config?.id) {
        await api.delete(`/model-config/${config.id}`);
      } else {
        await api.delete(`/model-config/agent/${encodeURIComponent(agentName)}`);
      }

      if (configForm.agentName === agentName) {
        resetConfigForm();
      }

      await loadConfigs();
      setSuccess(`Конфиг агента "${agentName}" удалён.`);
    } catch (error) {
      handleRequestError(error, "Не удалось удалить конфиг агента");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleCreateCollection = async (event) => {
    event.preventDefault();
    const collectionName = newCollectionName.trim();

    if (!collectionName) {
      setFailure("Укажите имя коллекции.");
      return;
    }

    setCollectionActionLoading(true);
    clearStatus();

    try {
      await api.post("/vector-store/create", { collectionName });
      setNewCollectionName("");
      await loadCollections();
      setSelectedCollectionName(collectionName);
      setConfigForm((prev) =>
        prev.vectorStoreName ? prev : { ...prev, vectorStoreName: collectionName }
      );
      setSuccess(`Коллекция "${collectionName}" создана.`);
    } catch (error) {
      handleRequestError(error, "Не удалось создать коллекцию");
    } finally {
      setCollectionActionLoading(false);
    }
  };

  const handleDeleteCollection = async (collectionName) => {
    if (!window.confirm(`Удалить коллекцию "${collectionName}"?`)) {
      return;
    }

    setCollectionActionLoading(true);
    clearStatus();

    try {
      await api.delete("/vector-store", {
        data: { collectionName },
      });

      if (selectedCollectionName === collectionName) {
        setSelectedCollectionName("");
        setDocs([]);
      }

      if (configForm.vectorStoreName === collectionName) {
        setConfigForm((prev) => ({ ...prev, vectorStoreName: "" }));
      }

      await loadCollections();
      setSuccess(`Коллекция "${collectionName}" удалена.`);
    } catch (error) {
      handleRequestError(error, "Не удалось удалить коллекцию");
    } finally {
      setCollectionActionLoading(false);
    }
  };

  const handleUploadFiles = async (event) => {
    event.preventDefault();

    if (!selectedCollectionName) {
      setFailure("Сначала выберите коллекцию.");
      return;
    }

    if (!selectedFiles.length) {
      setFailure("Выберите хотя бы один файл.");
      return;
    }

    setUploadingFiles(true);
    setUploadProgress(initialUploadProgress);
    clearStatus();

    try {
      const fileBatches = chunkArray(selectedFiles, UPLOAD_BATCH_SIZE);
      const totalFiles = selectedFiles.length;
      let uploadedFilesCount = 0;

      setUploadProgress({
        ...initialUploadProgress,
        active: true,
        totalFiles,
        totalBatches: fileBatches.length,
      });

      for (let index = 0; index < fileBatches.length; index += 1) {
        const batchFiles = fileBatches[index];
        const batchBytes = getFilesTotalSize(batchFiles);
        const completedBeforeBatch = uploadedFilesCount;
        const formData = new FormData();
        batchFiles.forEach((file) => {
          formData.append("files", file);
        });

        setStatus({
          type: "",
          text: `Загрузка файлов: пачка ${index + 1} из ${fileBatches.length}`,
        });
        setUploadProgress((prev) => ({
          ...prev,
          active: true,
          done: false,
          totalFiles,
          uploadedFiles: completedBeforeBatch,
          totalBatches: fileBatches.length,
          currentBatch: index + 1,
          currentBatchFiles: batchFiles.length,
          batchPercent: 0,
          totalPercent: getUploadPercent(completedBeforeBatch, totalFiles),
        }));

        await api.post(
          `/vector-store/files?collectionName=${encodeURIComponent(selectedCollectionName)}`,
          formData,
          {
            onUploadProgress: (progressEvent) => {
              const loaded = Number(progressEvent.loaded || 0);
              const total = Number(progressEvent.total || batchBytes || 0);
              const batchPercent = total > 0
                ? Math.min(100, Math.round((loaded / total) * 100))
                : 0;
              const estimatedUploadedFiles = completedBeforeBatch + (batchFiles.length * batchPercent / 100);

              setUploadProgress((prev) => ({
                ...prev,
                active: true,
                done: false,
                totalFiles,
                uploadedFiles: completedBeforeBatch,
                totalBatches: fileBatches.length,
                currentBatch: index + 1,
                currentBatchFiles: batchFiles.length,
                batchPercent,
                totalPercent: Math.min(99, getUploadPercent(estimatedUploadedFiles, totalFiles)),
              }));
            },
          }
        );

        const completedAfterBatch = completedBeforeBatch + batchFiles.length;
        uploadedFilesCount = completedAfterBatch;
        setUploadProgress((prev) => ({
          ...prev,
          active: true,
          done: false,
          totalFiles,
          uploadedFiles: completedAfterBatch,
          totalBatches: fileBatches.length,
          currentBatch: index + 1,
          currentBatchFiles: batchFiles.length,
          batchPercent: 100,
          totalPercent: getUploadPercent(completedAfterBatch, totalFiles),
        }));
      }

      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await loadDocs(selectedCollectionName);
      setUploadProgress((prev) => ({
        ...prev,
        active: false,
        done: true,
        uploadedFiles: totalFiles,
        batchPercent: 100,
        totalPercent: 100,
      }));
      setSuccess(`Файлы загружены в "${selectedCollectionName}".`);
    } catch (error) {
      setUploadProgress((prev) => ({
        ...prev,
        active: false,
        done: false,
      }));
      handleRequestError(error, "Не удалось загрузить файлы");
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDeleteDoc = async (fileName) => {
    if (!selectedCollectionName) {
      return;
    }

    if (!window.confirm(`Удалить файл "${fileName}" из коллекции "${selectedCollectionName}"?`)) {
      return;
    }

    setCollectionActionLoading(true);
    clearStatus();

    try {
      await api.delete("/vector-store/file", {
        data: {
          collectionName: selectedCollectionName,
          fileName,
        },
      });

      await loadDocs(selectedCollectionName);
      setSuccess(`Файл "${fileName}" удалён.`);
    } catch (error) {
      handleRequestError(error, "Не удалось удалить файл");
    } finally {
      setCollectionActionLoading(false);
    }
  };

  return (
    <div style={pageShell}>
      <div style={pageWrap}>
        <section style={heroCard}>
          <div>
            <div style={eyebrow}>Admin / Agents</div>
            <h1 style={pageTitle}>Управление агентами и vector store</h1>
            <p style={pageSubtitle}>
              Один экран для CRUD конфигов агентов, привязки моделей и коллекций,
              загрузки документов и контроля содержимого vector store.
            </p>
          </div>

          <div style={statsGrid}>
            <StatCard label="Конфиги агентов" value={activeConfigsCount} />
            <StatCard label="Коллекции" value={activeCollectionsCount} />
            <StatCard label="Документы в выбранной" value={docs.length} />
            <StatCard
              label="LLM провайдеры готовы"
              value={Object.values(providerStatusesMap).filter((item) => item?.available).length}
            />
          </div>
        </section>

        {(status.text || pageLoading) && (
          <section
            style={{
              ...panel,
              ...(status.type === "error" ? errorPanel : status.type === "success" ? successPanel : {}),
            }}
          >
            {status.text || "Загрузка данных..."}
          </section>
        )}

        <section style={gridTwoColumns}>
          <div style={panel}>
            <div style={sectionHead}>
              <div>
                <div style={sectionTitle}>Конфиг агента</div>
                <div style={sectionHint}>
                  POST `/model-config` создаёт и обновляет конфиг в одном запросе.
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  resetConfigForm();
                  clearStatus();
                }}
                style={btnGhost}
              >
                Очистить форму
              </button>
            </div>

            <form onSubmit={handleSaveConfig} style={formGrid}>
              <label style={field}>
                <span style={labelText}>Имя агента</span>
                <input
                  list="agent-names"
                  value={configForm.agentName}
                  onChange={handleConfigInputChange("agentName")}
                  placeholder="например: quick_all"
                  style={input}
                />
              </label>

              <label style={field}>
                <span style={labelText}>Provider</span>
                <select
                  value={configForm.provider}
                  onChange={handleProviderChange}
                  style={input}
                >
                  {providerOptions.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                {selectedProviderStatus && (
                  <span
                    style={{
                      ...metaText,
                      color: selectedProviderStatus.available ? "#047857" : "#be123c",
                    }}
                  >
                    {selectedProviderStatus.available
                      ? `Готов · моделей: ${selectedProviderStatus.models?.length || 0}${
                          configForm.provider === "local-qwen" && localQueue
                            ? ` · очередь ${localQueue.queued}/${localQueue.limit}, активно ${localQueue.active}`
                            : ""
                        }`
                      : selectedProviderStatus.error?.message || "Провайдер недоступен"}
                  </span>
                )}
              </label>

              <label style={field}>
                <span style={labelText}>Model</span>
                <select
                  value={configForm.model}
                  onChange={handleConfigInputChange("model")}
                  style={input}
                >
                  {Object.entries(modelOptions).length === 0 ? (
                    <option value="">Models not loaded</option>
                  ) : (
                    Object.entries(modelOptions).map(([id, modelName]) => (
                      <option key={id} value={modelName}>
                        {modelName}
                      </option>
                    ))
                  )}
                </select>
              </label>

              <label style={fieldFull}>
                <span style={labelText}>Vector store</span>
                <div style={inlineRow}>
                  <input
                    list="collection-names"
                    value={configForm.vectorStoreName}
                    onChange={handleConfigInputChange("vectorStoreName")}
                    placeholder="например: tax_docs"
                    style={{ ...input, flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setConfigForm((prev) => ({
                        ...prev,
                        vectorStoreName: selectedCollectionName || prev.vectorStoreName,
                      }))
                    }
                    disabled={!selectedCollectionName}
                    style={btnGhost}
                  >
                    Взять выбранную
                  </button>
                </div>
                <span style={metaText}>
                  Необязательно. Для Local Qwen поиск выполняется на AI-сервере через OpenAI embeddings и Chroma,
                  затем найденный контекст передаётся локальной модели.
                </span>
              </label>

              <label style={fieldFull}>
                <span style={labelText}>Инструкция</span>
                <textarea
                  value={configForm.instruction}
                  onChange={handleConfigInputChange("instruction")}
                  placeholder="Опишите системную инструкцию для агента"
                  rows={8}
                  style={textarea}
                />
              </label>

              <div style={actionsRow}>
                <button
                  type="submit"
                  disabled={
                    savingConfig ||
                    pageLoading ||
                    selectedProviderStatus?.available === false ||
                    !configForm.model
                  }
                  style={btnPrimary}
                >
                  {savingConfig
                    ? "Сохранение..."
                    : configForm.id
                      ? "Обновить конфиг"
                      : "Создать конфиг"}
                </button>
                <button
                  type="button"
                  onClick={loadConfigs}
                  disabled={configsLoading || pageLoading}
                  style={btnGhost}
                >
                  Обновить справочники
                </button>
              </div>
            </form>

            <datalist id="agent-names">
              {agentOptions.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>

            <datalist id="collection-names">
              {collectionOptions.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
          </div>

          <div style={panel}>
            <div style={sectionHead}>
              <div>
                <div style={sectionTitle}>Список конфигов</div>
                <div style={sectionHint}>
                  Доступные chat type конфиги: {Object.keys(chatTypeConfigs || {}).length}
                </div>
              </div>
              <button
                type="button"
                onClick={loadConfigs}
                disabled={configsLoading || pageLoading}
                style={btnGhost}
              >
                {configsLoading ? "Обновление..." : "Обновить"}
              </button>
            </div>

            {configs.length === 0 ? (
              <div style={emptyState}>Конфиги агентов пока не созданы.</div>
            ) : (
              <div style={configListGrid}>
                {configs.map((config) => {
                  const configKey = `${config.id || config.agentName}`;
                  const isExpanded = !!expandedConfigs[configKey];
                  const instructionText = config.instruction || "Инструкция не задана";
                  const shouldClamp = instructionText.length > 180;

                  return (
                  <article key={configKey} style={configCard}>
                    <div style={compactCardHead}>
                      <div>
                        <div style={entityTitle}>{config.agentName || "Без имени"}</div>
                        <div style={compactMetaRow}>
                          <span style={metaPillMuted}>{config.provider || DEFAULT_PROVIDER}</span>
                          <span style={metaPill}>{config.model || "модель не указана"}</span>
                          <span style={metaPillMuted}>
                            {config.vectorStoreName || "без vector store"}
                          </span>
                        </div>
                      </div>
                      <div style={badge}>{config.id ? `#${config.id}` : "upsert"}</div>
                    </div>

                    <div style={compactSectionLabel}>Инструкция</div>
                    <div
                      style={
                        isExpanded
                          ? instructionPreviewExpanded
                          : instructionPreviewCompact
                      }
                    >
                      {instructionText}
                    </div>

                    <div style={compactFooter}>
                      <div style={metaText}>Создан: {formatDate(config.createdAt)}</div>

                      {shouldClamp ? (
                        <button
                          type="button"
                          onClick={() => toggleConfigExpand(configKey)}
                          style={btnText}
                        >
                          {isExpanded ? "Свернуть" : "Показать"}
                        </button>
                      ) : null}
                    </div>

                    <div style={compactActionsRow}>
                      <button
                        type="button"
                        onClick={() => handleEditConfig(config)}
                        style={btnGhostSmall}
                      >
                        Редактировать
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteConfig(config)}
                        disabled={savingConfig}
                        style={btnDangerSmall}
                      >
                        Удалить
                      </button>
                    </div>
                  </article>
                );
                })}
              </div>
            )}
          </div>
        </section>

        <section style={gridTwoColumns}>
          <div style={panel}>
            <div style={sectionHead}>
              <div>
                <div style={sectionTitle}>Коллекции vector store</div>
                <div style={sectionHint}>
                  Все admin-роуты уже работают в scope `agent`.
                </div>
              </div>
              <button
                type="button"
                onClick={loadCollections}
                disabled={collectionsLoading || pageLoading}
                style={btnGhost}
              >
                {collectionsLoading ? "Обновление..." : "Обновить"}
              </button>
            </div>

            <form onSubmit={handleCreateCollection} style={stackedForm}>
              <label style={field}>
                <span style={labelText}>Новая коллекция</span>
                <input
                  value={newCollectionName}
                  onChange={(event) => setNewCollectionName(event.target.value)}
                  placeholder="например: tax_docs"
                  style={input}
                />
              </label>

              <button
                type="submit"
                disabled={collectionActionLoading || pageLoading}
                style={btnPrimary}
              >
                {collectionActionLoading ? "Создание..." : "Создать коллекцию"}
              </button>
            </form>

            {collections.length === 0 ? (
              <div style={emptyState}>Коллекции пока не созданы.</div>
            ) : (
              <div style={listWrap}>
                {collections.map((collection) => {
                  const collectionName = getCollectionName(collection);
                  const isSelected = selectedCollectionName === collectionName;

                  return (
                    <article
                      key={`${collection.id || collectionName}`}
                      style={{
                        ...listCard,
                        ...(isSelected ? selectedCard : {}),
                      }}
                    >
                      <div style={listCardHead}>
                        <div>
                          <div style={entityTitle}>{collectionName}</div>
                          <div style={metaText}>
                            scope: {collection.scope || "agent"} • owner:{" "}
                            {collection.owner_user_id || "admin"}
                          </div>
                        </div>
                        <div style={badgeMuted}>
                          {collection.is_deleted ? "deleted" : "active"}
                        </div>
                      </div>

                      <div style={metaText}>
                        Создана: {formatDate(collection.created_at)}
                      </div>

                      <div style={actionsRow}>
                        <button
                          type="button"
                          onClick={() => setSelectedCollectionName(collectionName)}
                          style={isSelected ? btnPrimary : btnGhost}
                        >
                          {isSelected ? "Выбрана" : "Открыть"}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setConfigForm((prev) => ({
                              ...prev,
                              vectorStoreName: collectionName,
                            }))
                          }
                          style={btnGhost}
                        >
                          В форму агента
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCollection(collectionName)}
                          disabled={collectionActionLoading}
                          style={btnDanger}
                        >
                          Удалить
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>

          <div style={panel}>
            <div style={sectionHead}>
              <div>
                <div style={sectionTitle}>Документы коллекции</div>
                <div style={sectionHint}>
                  {selectedCollectionName
                    ? `Сейчас открыта коллекция "${selectedCollectionName}".`
                    : "Выберите коллекцию слева, чтобы увидеть документы."}
                </div>
              </div>
              <button
                type="button"
                onClick={() => loadDocs(selectedCollectionName).catch((error) => handleRequestError(error))}
                disabled={!selectedCollectionName || docsLoading || pageLoading}
                style={btnGhost}
              >
                {docsLoading ? "Загрузка..." : "Обновить документы"}
              </button>
            </div>

            {!selectedCollectionName ? (
              <div style={emptyState}>Коллекция ещё не выбрана.</div>
            ) : (
              <>
                <div style={selectedCollectionBox}>
                  <div style={entityTitle}>{selectedCollectionName}</div>
                  <div style={metaText}>
                    Статус: {selectedCollectionMeta?.is_deleted ? "удалена" : "активна"} •
                    документов: {docs.length}
                  </div>
                </div>

                <form onSubmit={handleUploadFiles} style={stackedForm}>
                  <label style={field}>
                    <span style={labelText}>Добавить файлы</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={(event) => {
                        setSelectedFiles(Array.from(event.target.files || []));
                        setUploadProgress(initialUploadProgress);
                      }}
                      style={inputFile}
                    />
                  </label>

                  <div style={metaText}>
                    Выбрано файлов: {selectedFiles.length}
                  </div>

                  {(uploadProgress.active || uploadProgress.done) && (
                    <UploadProgress progress={uploadProgress} />
                  )}

                  <button
                    type="submit"
                    disabled={uploadingFiles || pageLoading}
                    style={btnSuccess}
                  >
                    {uploadingFiles ? "Загрузка..." : "Загрузить файлы"}
                  </button>
                </form>

                {docsLoading ? (
                  <div style={emptyState}>Загружаю список документов...</div>
                ) : docs.length === 0 ? (
                  <div style={emptyState}>В коллекции пока нет документов.</div>
                ) : (
                  <div style={listWrap}>
                    {docs.map((doc) => (
                      <article key={`${doc.id || doc.doc_name}`} style={listCard}>
                        <div style={listCardHead}>
                          <div>
                            <div style={entityTitle}>{doc.doc_name}</div>
                            <div style={metaText}>
                              Добавлен: {formatDate(doc.created_at)}
                            </div>
                          </div>
                          <div style={badgeMuted}>
                            {doc.is_deleted ? "deleted" : "active"}
                          </div>
                        </div>

                        <div style={actionsRow}>
                          <button
                            type="button"
                            onClick={() => handleDeleteDoc(doc.doc_name)}
                            disabled={collectionActionLoading || !!doc.is_deleted}
                            style={btnDanger}
                          >
                            Удалить файл
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={statCard}>
      <div style={statValue}>{value}</div>
      <div style={statLabel}>{label}</div>
    </div>
  );
}

function UploadProgress({ progress }) {
  const totalPercent = Math.max(0, Math.min(100, Number(progress.totalPercent || 0)));
  const batchPercent = Math.max(0, Math.min(100, Number(progress.batchPercent || 0)));

  return (
    <div style={uploadProgressBox}>
      <div style={uploadProgressHead}>
        <div style={entityTitle}>{totalPercent}%</div>
        <div style={metaText}>
          {progress.uploadedFiles} / {progress.totalFiles} файлов
        </div>
      </div>
      <div style={progressTrack}>
        <div style={{ ...progressFill, width: `${totalPercent}%` }} />
      </div>
      <div style={uploadProgressMeta}>
        <span>
          Пачка {progress.currentBatch || 0} из {progress.totalBatches || 0}
        </span>
        <span>
          текущая загрузка: {batchPercent}%
        </span>
      </div>
      <div style={metaText}>
        {progress.done
          ? "Файлы отправлены. Индексация продолжается на сервере."
          : "Отправляю файлы на сервер и ставлю их в очередь индексации."}
      </div>
    </div>
  );
}

function getPayloadData(payload) {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  if (payload.success === false || payload.ok === false) {
    throw new Error(payload.error || payload.message || "Request failed");
  }

  return payload.data;
}

function getErrorMessage(error, fallback = "Что-то пошло не так") {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

function uniqueNonEmpty(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

function chunkArray(items, size) {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

function getFilesTotalSize(files) {
  return files.reduce((sum, file) => sum + Number(file?.size || 0), 0);
}

function getUploadPercent(uploadedFiles, totalFiles) {
  if (!totalFiles) {
    return 0;
  }

  return Math.max(0, Math.min(100, Math.round((uploadedFiles / totalFiles) * 100)));
}

function getCollectionName(collection) {
  return (
    collection?.collectionName ||
    collection?.storedCollectionName ||
    collection?.name ||
    ""
  );
}

function isAdminCollection(collection) {
  const owner =
    collection?.owner_user_id ??
    collection?.ownerUserId ??
    collection?.owner_user ??
    collection?.owner;

  return owner === undefined || owner === null || owner === "" || owner === "admin";
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

const pageShell = {
  minHeight: "100vh",
  background:
    "linear-gradient(180deg, #eff6ff 0%, #f8fafc 18%, #f8fafc 100%)",
  color: "#0f172a",
};

const pageWrap = {
  maxWidth: 1380,
  margin: "0 auto",
  padding: 24,
  display: "grid",
  gap: 20,
};

const heroCard = {
  background:
    "linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.95) 100%)",
  color: "#f8fafc",
  borderRadius: 24,
  padding: 24,
  boxShadow: "0 20px 45px rgba(15, 23, 42, 0.18)",
  display: "grid",
  gap: 20,
};

const eyebrow = {
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.18em",
  color: "#93c5fd",
  marginBottom: 10,
};

const pageTitle = {
  margin: 0,
  fontSize: "clamp(28px, 4vw, 40px)",
  lineHeight: 1.05,
};

const pageSubtitle = {
  margin: "10px 0 0",
  maxWidth: 760,
  fontSize: 15,
  lineHeight: 1.6,
  color: "#cbd5e1",
};

const statsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};

const statCard = {
  background: "rgba(148, 163, 184, 0.12)",
  border: "1px solid rgba(148, 163, 184, 0.2)",
  borderRadius: 18,
  padding: 16,
};

const statValue = {
  fontSize: 28,
  fontWeight: 700,
};

const statLabel = {
  marginTop: 6,
  fontSize: 13,
  color: "#cbd5e1",
};

const gridTwoColumns = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
  gap: 20,
  alignItems: "start",
};

const panel = {
  background: "#ffffff",
  borderRadius: 24,
  border: "1px solid #dbe4f0",
  boxShadow: "0 16px 32px rgba(148, 163, 184, 0.12)",
  padding: 20,
  display: "grid",
  gap: 18,
};

const successPanel = {
  background: "#ecfdf5",
  borderColor: "#a7f3d0",
  color: "#065f46",
};

const errorPanel = {
  background: "#fff1f2",
  borderColor: "#fecdd3",
  color: "#be123c",
};

const sectionHead = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
};

const sectionTitle = {
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1.1,
};

const sectionHint = {
  marginTop: 6,
  fontSize: 13,
  color: "#64748b",
  lineHeight: 1.5,
};

const formGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 14,
};

const stackedForm = {
  display: "grid",
  gap: 14,
};

const field = {
  display: "grid",
  gap: 8,
};

const fieldFull = {
  display: "grid",
  gap: 8,
  gridColumn: "1 / -1",
};

const labelText = {
  fontSize: 13,
  fontWeight: 700,
  color: "#334155",
};

const input = {
  width: "100%",
  minHeight: 46,
  padding: "12px 14px",
  borderRadius: 14,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  outline: "none",
  boxSizing: "border-box",
};

const inputFile = {
  ...input,
  padding: "10px 12px",
};

const textarea = {
  ...input,
  minHeight: 180,
  resize: "vertical",
  fontFamily: "inherit",
};

const inlineRow = {
  display: "flex",
  gap: 10,
  alignItems: "center",
  flexWrap: "wrap",
};

const actionsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const listWrap = {
  display: "grid",
  gap: 12,
};

const configListGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 12,
};

const listCard = {
  border: "1px solid #e2e8f0",
  borderRadius: 18,
  padding: 16,
  display: "grid",
  gap: 12,
  background: "#f8fafc",
};

const configCard = {
  ...listCard,
  padding: 14,
  gap: 10,
  alignContent: "start",
  background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
};

const selectedCard = {
  borderColor: "#0f172a",
  background: "#eef4ff",
};

const listCardHead = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
};

const compactCardHead = {
  ...listCardHead,
  alignItems: "center",
};

const entityTitle = {
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.25,
  wordBreak: "break-word",
};

const metaText = {
  fontSize: 12,
  color: "#64748b",
  lineHeight: 1.5,
};

const compactMetaRow = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
  marginTop: 8,
};

const metaPill = {
  padding: "4px 8px",
  borderRadius: 999,
  background: "#dbeafe",
  color: "#1e40af",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.2,
};

const metaPillMuted = {
  ...metaPill,
  background: "#e2e8f0",
  color: "#334155",
};

const badge = {
  padding: "6px 10px",
  borderRadius: 999,
  background: "#dbeafe",
  color: "#1d4ed8",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const badgeMuted = {
  ...badge,
  background: "#e2e8f0",
  color: "#334155",
};

const instructionPreview = {
  fontSize: 14,
  lineHeight: 1.6,
  color: "#1e293b",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const compactSectionLabel = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#64748b",
};

const instructionPreviewCompact = {
  fontSize: 13,
  lineHeight: 1.55,
  color: "#1e293b",
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  wordBreak: "break-word",
  minHeight: "3.8em",
};

const instructionPreviewExpanded = {
  ...instructionPreview,
  fontSize: 13,
  lineHeight: 1.55,
};

const compactFooter = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  flexWrap: "wrap",
};

const compactActionsRow = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const selectedCollectionBox = {
  borderRadius: 18,
  padding: 16,
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  display: "grid",
  gap: 6,
};

const uploadProgressBox = {
  borderRadius: 16,
  padding: 14,
  background: "#f8fafc",
  border: "1px solid #dbe4f0",
  display: "grid",
  gap: 10,
};

const uploadProgressHead = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const progressTrack = {
  height: 10,
  borderRadius: 999,
  background: "#e2e8f0",
  overflow: "hidden",
};

const progressFill = {
  height: "100%",
  borderRadius: 999,
  background: "#059669",
  transition: "width 180ms ease",
};

const uploadProgressMeta = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  fontSize: 12,
  color: "#475569",
};

const emptyState = {
  borderRadius: 18,
  padding: 18,
  background: "#f8fafc",
  border: "1px dashed #cbd5e1",
  color: "#64748b",
  lineHeight: 1.6,
};

const btnBase = {
  minHeight: 42,
  padding: "10px 14px",
  borderRadius: 14,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  cursor: "pointer",
  fontWeight: 600,
};

const btnGhost = {
  ...btnBase,
  background: "#fff",
};

const btnPrimary = {
  ...btnBase,
  background: "#0f172a",
  color: "#fff",
  borderColor: "#0f172a",
};

const btnSuccess = {
  ...btnBase,
  background: "#059669",
  color: "#fff",
  borderColor: "#059669",
};

const btnDanger = {
  ...btnBase,
  background: "#e11d48",
  color: "#fff",
  borderColor: "#e11d48",
};

const btnGhostSmall = {
  ...btnGhost,
  minHeight: 36,
  padding: "8px 12px",
  borderRadius: 12,
  fontSize: 13,
};

const btnDangerSmall = {
  ...btnDanger,
  minHeight: 36,
  padding: "8px 12px",
  borderRadius: 12,
  fontSize: 13,
};

const btnText = {
  border: "none",
  background: "transparent",
  padding: 0,
  color: "#2563eb",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 700,
};
